import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MapPin, Briefcase, DollarSign, Phone, Mail } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJob();
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      setJob(response.data);
    } catch (error) {
      console.error("Failed to fetch job", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!job) {
    return <div className="text-center py-12">Job not found</div>;
  }

  const getJobTypeColor = (type) => {
    const colors = {
      "Full-Time": "bg-blue-100 text-blue-700",
      "Part-Time": "bg-green-100 text-green-700",
      "Internship": "bg-purple-100 text-purple-700",
      "Apprenticeship": "bg-orange-100 text-orange-700"
    };
    return colors[type] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      <Button
        variant="ghost"
        onClick={() => navigate("/jobs")}
        className="mb-4"
        data-testid="back-to-jobs-button"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Jobs
      </Button>

      <Card className="shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-teal-50">
          <div className="flex justify-between items-start gap-4">
            <div>
              <CardTitle className="text-3xl mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                {job.title}
              </CardTitle>
              <p className="text-xl text-gray-700 font-medium">{job.company}</p>
            </div>
            <Badge className={`${getJobTypeColor(job.job_type)} text-base px-3 py-1`}>
              {job.job_type}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 mt-1" style={{ color: '#4ECDC4' }} />
              <div>
                <p className="font-medium text-gray-900">Location</p>
                <p className="text-gray-600">{job.city}, {job.county} County</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Briefcase className="h-5 w-5 mt-1" style={{ color: '#4ECDC4' }} />
              <div>
                <p className="font-medium text-gray-900">Trade Type</p>
                <p className="text-gray-600">{job.trade_type}</p>
              </div>
            </div>
            {job.pay_range && (
              <div className="flex items-start gap-3">
                <DollarSign className="h-5 w-5 mt-1" style={{ color: '#FF6B6B' }} />
                <div>
                  <p className="font-medium text-gray-900">Pay Range</p>
                  <p className="text-gray-600">{job.pay_range}</p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <div className="h-5 w-5 mt-1 flex items-center justify-center rounded-full" style={{ backgroundColor: '#4ECDC4' }}>
                <span className="text-white text-xs">📅</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Posted</p>
                <p className="text-gray-600">{formatDistanceToNow(new Date(job.posted_date))} ago</p>
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Job Description</h3>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{job.description}</p>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Required Skills</h3>
            <p className="text-gray-700 leading-relaxed">{job.skills}</p>
          </div>

          <div className="border-t pt-6 bg-gradient-to-r from-orange-50 to-teal-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>How to Apply</h3>
            <p className="text-gray-700 leading-relaxed" data-testid="apply-info">{job.apply_info}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}